import React, { memo, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import ContentContainer from "../../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import Table from "ui-components/table/table";
import { useDispatchBoardQueue } from "./hooks/useDispatchBoardQueue";
import UserState from "slices/user/UserState";
import { userSelector } from "slices/user/userSlice";
import { getCityDeliveryDispatchBoardQueueColumns } from "./cityDeliveryDispatchBoardQueueColumns";
import "./cityDeliveryDispatchBoard.scss";
import {
  DispatchBoardQueueLoad,
  DispatchBoardQueueStatus,
} from "app/data/city-delivery/dispatch-board/models";
import CityDeliveryDispatchWorkflow from "./workflow/cityDeliveryDispatchWorkflow";
import ReloadButton from "ui-components/reload-button/reloadButton";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import LabeledDateRangeInput from "ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import Button, { ButtonThemes } from "ui-components/button/button";
import moment from "moment";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";

const STATUS_OPTIONS = [
  { value: DispatchBoardQueueStatus.READY_TO_ASSIGN, label: "Ready to Assign" },
  { value: DispatchBoardQueueStatus.LOADING, label: "Loading" },
  { value: DispatchBoardQueueStatus.READY_TO_CLOSE, label: "Ready to Close" },
  { value: DispatchBoardQueueStatus.READY_TO_DISPATCH, label: "Ready to Dispatch" },
  { value: DispatchBoardQueueStatus.DISPATCHED, label: "Dispatched" },
];

const DEFAULT_STATUS_OPTIONS = STATUS_OPTIONS.filter(
  (o) => o.value !== DispatchBoardQueueStatus.DISPATCHED
);

function matchesSearch(value: string | number | undefined, search: string): boolean {
  if (!search.trim()) return true;
  const str = value != null ? String(value).toLowerCase() : "";
  return str.includes(search.trim().toLowerCase());
}

function matchesSearchAny(values: (string | number | undefined)[], search: string): boolean {
  if (!search.trim()) return true;
  const q = search.trim().toLowerCase();
  return values.some((v) => v != null && String(v).toLowerCase().includes(q));
}

function parseDateSafe(
  d: Date | string | undefined
): Date | undefined {
  if (d == null) return undefined;
  const m = moment(d, moment.ISO_8601);
  return m.isValid() ? m.toDate() : undefined;
}

function isDateInRange(
  value: Date | string | undefined,
  rangeStart: string,
  rangeEnd: string
): boolean {
  if (!rangeStart && !rangeEnd) return true;
  const date = parseDateSafe(value);
  if (!date) return false;
  if (rangeStart) {
    const start = moment(rangeStart).startOf("day");
    if (date < start.toDate()) return false;
  }
  if (rangeEnd) {
    const end = moment(rangeEnd).endOf("day");
    if (date > end.toDate()) return false;
  }
  return true;
}

function isEtaInRange(
  earliestEta: Date | string | undefined,
  latestEta: Date | string | undefined,
  rangeStart: string,
  rangeEnd: string
): boolean {
  if (!rangeStart && !rangeEnd) return true;
  const earliest = parseDateSafe(earliestEta);
  const latest = parseDateSafe(latestEta);
  if (!earliest && !latest) return false;
  const start = rangeStart ? moment(rangeStart).startOf("day").toDate() : null;
  const end = rangeEnd ? moment(rangeEnd).endOf("day").toDate() : null;
  if (start && latest && latest < start) return false;
  if (end && earliest && earliest > end) return false;
  return true;
}

const CityDeliveryDispatchBoard: React.FC<{}> = memo(() => {
  const [selectedDispatchLoad, setSelectedDispatchLoad] = useState<DispatchBoardQueueLoad>();
  const userState: UserState = useSelector(userSelector);
  const activeTerminalId = userState.activeTerminal?.id;

  const [picklistManifestSearch, setPicklistManifestSearch] = useState("");
  const [statusSelectedOptions, setStatusSelectedOptions] = useState<XGSSelectOption[]>(DEFAULT_STATUS_OPTIONS);
  const [doorSearch, setDoorSearch] = useState("");
  const [routeNameSearch, setRouteNameSearch] = useState("");
  const [driverSearch, setDriverSearch] = useState("");
  const [carrierSearch, setCarrierSearch] = useState("");
  const [dispatchedAtStart, setDispatchedAtStart] = useState("");
  const [dispatchedAtEnd, setDispatchedAtEnd] = useState("");
  const [etaStart, setEtaStart] = useState("");
  const [etaEnd, setEtaEnd] = useState("");
  const [showAllFilters, setShowAllFilters] = useState(false);

  const {
    dispatchBoardQueueData,
    isLoadingDispatchBoardQueue,
    refetchDispatchBoardQueue,
    lastUpdated
  } = useDispatchBoardQueue({
    terminalNumber: activeTerminalId,
  });

  const filteredLoads = useMemo(() => {
    const rawLoads = dispatchBoardQueueData?.data?.loads ?? [];
    const statusSet = new Set(statusSelectedOptions.map((o) => String(o.value)));
    return rawLoads.filter((load: DispatchBoardQueueLoad) => {
      if (picklistManifestSearch.trim()) {
        const pl = load.picklistNumber != null ? String(load.picklistNumber) : "";
        const mf = load.manifestNumber != null ? String(load.manifestNumber) : "";
        const q = picklistManifestSearch.trim().toLowerCase();
        if (!pl.toLowerCase().includes(q) && !mf.toLowerCase().includes(q)) return false;
      }
      if (statusSet.size > 0 && !statusSet.has(load.status)) return false;
      if (!matchesSearch(load.door, doorSearch)) return false;
      if (!matchesSearch(load.routeName, routeNameSearch)) return false;
      if (!matchesSearchAny([load.driverName, load.driverNumber], driverSearch)) return false;
      if (!matchesSearchAny([load.carrierName, load.carrierNumber], carrierSearch)) return false;
      if (!isDateInRange(load.dispatchedAt, dispatchedAtStart, dispatchedAtEnd)) return false;
      if (!isEtaInRange(load.earliestEta, load.latestEta, etaStart, etaEnd)) return false;
      return true;
    });
  }, [
    dispatchBoardQueueData?.data?.loads,
    picklistManifestSearch,
    statusSelectedOptions,
    doorSearch,
    routeNameSearch,
    driverSearch,
    carrierSearch,
    dispatchedAtStart,
    dispatchedAtEnd,
    etaStart,
    etaEnd,
  ]);

  const hasActiveFilters =
    !!picklistManifestSearch.trim() ||
    !!doorSearch.trim() ||
    !!routeNameSearch.trim() ||
    !!driverSearch.trim() ||
    !!carrierSearch.trim() ||
    !!dispatchedAtStart ||
    !!dispatchedAtEnd ||
    !!etaStart ||
    !!etaEnd ||
    statusSelectedOptions.length > 0;

  const clearFilters = () => {
    setPicklistManifestSearch("");
    setStatusSelectedOptions([]);
    setDoorSearch("");
    setRouteNameSearch("");
    setDriverSearch("");
    setCarrierSearch("");
    setDispatchedAtStart("");
    setDispatchedAtEnd("");
    setEtaStart("");
    setEtaEnd("");
  };

  const formattedTime = lastUpdated
    ? (() => {
      const date = new Date(lastUpdated);

      const datePart = date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });

      const timePart = date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      return `${datePart} • ${timePart}`;
    })()
    : null;


  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ 'full-width': true }}
      titleComponent={
        <ContentContainerToolbar
          title="City Delivery Dispatch Board"
          mods={{ "full-width": true }}
          // style={{ justifyContent: "end", width: "100%" }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: "6px",
              justifyContent: "end",
              width: "100%"
            }}
          >
            {formattedTime && (
              <span style={{ fontSize: "14px", color: "#777" }}>
                Last Refreshed: {formattedTime}
              </span>
            )}
            <ReloadButton
              title="Refresh Dispatch"
              spinner={isLoadingDispatchBoardQueue}
              disabled={isLoadingDispatchBoardQueue}
              onClick={async () => {
                await refetchDispatchBoardQueue();
              }}
            />
          </div>
        </ContentContainerToolbar >
      }
    >
      <div className="xgs-city-delivery-dispatch-board">
        <div className="xgs-city-delivery-dispatch-board__controls">
          <div
            className={`xgs-city-delivery-dispatch-board__controls__search ${showAllFilters ? "xgs-city-delivery-dispatch-board__controls__search--expanded" : ""}`}
          >
            <div className="xgs-city-delivery-dispatch-board__controls__search-filters">
              <div className="xgs-city-delivery-dispatch-board__controls__item">
                <LabeledTextInput
                  label="Picklist / Manifest"
                  labelMode={LabelModes.column}
                  value={picklistManifestSearch}
                  onChange={(e) => setPicklistManifestSearch(e.currentTarget.value)}
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  type="text"
                  placeholder="PL # or MF #"
                />
              </div>
              <div className="xgs-city-delivery-dispatch-board__controls__item xgs-city-delivery-dispatch-board__controls__item--status">
                <LabeledSelectInput
                  label="Status"
                  labelMode={LabelModes.column}
                  isMulti
                  isClearable
                  isSearchable={false}
                  onMultiValuesChange={(options) =>
                    setStatusSelectedOptions(options ?? [])
                  }
                  value={statusSelectedOptions}
                  options={STATUS_OPTIONS}
                  placeholder="All statuses"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <div className="xgs-city-delivery-dispatch-board__controls__item">
                <LabeledTextInput
                  label="Door"
                  labelMode={LabelModes.column}
                  value={doorSearch}
                  onChange={(e) => setDoorSearch(e.currentTarget.value)}
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  type="text"
                  placeholder="Search door"
                />
              </div>
              <div className="xgs-city-delivery-dispatch-board__controls__item">
                <LabeledTextInput
                  label="Route Name"
                  labelMode={LabelModes.column}
                  value={routeNameSearch}
                  onChange={(e) => setRouteNameSearch(e.currentTarget.value)}
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  type="text"
                  placeholder="Search route"
                />
              </div>
              <div className="xgs-city-delivery-dispatch-board__controls__item">
                <LabeledTextInput
                  label="Driver"
                  labelMode={LabelModes.column}
                  value={driverSearch}
                  onChange={(e) => setDriverSearch(e.currentTarget.value)}
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  type="text"
                  placeholder="Search driver"
                />
              </div>
              <div className="xgs-city-delivery-dispatch-board__controls__item">
                <LabeledTextInput
                  label="Carrier"
                  labelMode={LabelModes.column}
                  value={carrierSearch}
                  onChange={(e) => setCarrierSearch(e.currentTarget.value)}
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  type="text"
                  placeholder="Search carrier"
                />
              </div>
              <div className="xgs-city-delivery-dispatch-board__controls__item xgs-city-delivery-dispatch-board__controls__item--date-range">
                <LabeledDateRangeInput
                  label="Dispatch Date"
                  labelMode={LabelModes.column}
                  start={dispatchedAtStart}
                  end={dispatchedAtEnd}
                  onStartChange={setDispatchedAtStart}
                  onEndChange={setDispatchedAtEnd}
                />
              </div>
              <div className="xgs-city-delivery-dispatch-board__controls__item xgs-city-delivery-dispatch-board__controls__item--date-range">
                <LabeledDateRangeInput
                  label="ETA Range"
                  labelMode={LabelModes.column}
                  start={etaStart}
                  end={etaEnd}
                  onStartChange={setEtaStart}
                  onEndChange={setEtaEnd}
                />
              </div>
            </div>
            <div className="xgs-city-delivery-dispatch-board__controls__search-actions">
              <div className="xgs-city-delivery-dispatch-board__controls__spacer" aria-hidden="true" />
              <div className="xgs-city-delivery-dispatch-board__controls__item xgs-city-delivery-dispatch-board__controls__item--button">
                <Button
                  theme={ButtonThemes.gray}
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                  type="button"
                >
                  Clear filters
                </Button>
              </div>
              <div className="xgs-city-delivery-dispatch-board__controls__item xgs-city-delivery-dispatch-board__controls__item--toggle">
                <button
                  type="button"
                  className="xgs-city-delivery-dispatch-board__controls__show-more"
                  onClick={() => setShowAllFilters((v) => !v)}
                >
                  {showAllFilters ? "Show less" : "Show more"}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="xgs-city-delivery-dispatch-board__table-wrapper">
          <Table
            isLoading={isLoadingDispatchBoardQueue}
            columns={getCityDeliveryDispatchBoardQueueColumns()}
            data={filteredLoads}
            ignoreCellClick="selection"
            cursorPointer={true}
            noResultsText="No data available"
            onSelectedRowsChange={null}
            onRowClicked={(row: DispatchBoardQueueLoad) => { setSelectedDispatchLoad(row) }}
            responsive={true}
            sorting={true}
            enableMultiSort={true}
            autoResetSelectedRows={true}
            keepTableOnLoading={true}
            autoHeight={true}
          />
        </div>
        {(selectedDispatchLoad && (selectedDispatchLoad.picklistNumber || selectedDispatchLoad.manifestNumber)) && (
          <CityDeliveryDispatchWorkflow
            dispatchLoad={selectedDispatchLoad}
            onClose={() => setSelectedDispatchLoad(undefined)}
            onLoadManifestNumberUpdate={(manifestNumber) =>
              setSelectedDispatchLoad((prev) =>
                prev ? { ...prev, manifestNumber, picklistNumber: undefined } : prev
              )
            }
          />
        )}
      </div>
    </ContentContainer >
  );
});

export default CityDeliveryDispatchBoard;
